import { Box } from '@mui/material'
import * as React from 'react'
import { ProductType } from '../../../all-types'
import ProductCard from './product-card'

interface VirtualizedProductGridProps {
  products: ProductType[]
  openPopUp: (p: ProductType) => void
}

const CARD_WIDTH = 176
const ROW_HEIGHT = 216
const OVERSCAN_ROWS = 3

const VirtualizedProductGrid: React.FC<VirtualizedProductGridProps> = ({
  products,
  openPopUp,
}) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const [viewport, setViewport] = React.useState({
    width: 0,
    height: 0,
    scrollTop: 0,
  })

  React.useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateViewport = () => {
      setViewport({
        width: container.clientWidth,
        height: container.clientHeight,
        scrollTop: container.scrollTop,
      })
    }

    updateViewport()

    const resizeObserver = new ResizeObserver(updateViewport)
    resizeObserver.observe(container)
    container.addEventListener('scroll', updateViewport, { passive: true })

    return () => {
      resizeObserver.disconnect()
      container.removeEventListener('scroll', updateViewport)
    }
  }, [])

  React.useEffect(() => {
    const container = containerRef.current
    if (!container) return

    if (container.scrollTop > 0) {
      container.scrollTop = 0
    }
  }, [products])

  const columns = Math.max(1, Math.floor(viewport.width / CARD_WIDTH))
  const totalRows = Math.ceil(products.length / columns)
  const startRow = Math.max(0, Math.floor(viewport.scrollTop / ROW_HEIGHT) - OVERSCAN_ROWS)
  const endRow = Math.min(
    totalRows,
    Math.ceil((viewport.scrollTop + viewport.height) / ROW_HEIGHT) + OVERSCAN_ROWS
  )
  const startIndex = startRow * columns
  const endIndex = Math.min(products.length, endRow * columns)
  const visibleProducts = products.slice(startIndex, endIndex)
  const gridWidth = columns * CARD_WIDTH

  return (
    <Box
      ref={containerRef}
      sx={{
        height: '100%',
        overflowY: 'auto',
        position: 'relative',
        scrollbarWidth: 'thin',
        width: '100%',
      }}
    >
      <Box
        sx={{
          height: totalRows * ROW_HEIGHT,
          mx: 'auto',
          position: 'relative',
          width: gridWidth,
        }}
      >
        {visibleProducts.map((product, offset) => {
          const index = startIndex + offset
          const row = Math.floor(index / columns)
          const column = index % columns

          return (
            <Box
              key={product._id}
              sx={{
                height: ROW_HEIGHT,
                left: column * CARD_WIDTH,
                position: 'absolute',
                top: row * ROW_HEIGHT,
                width: CARD_WIDTH,
              }}
            >
              <ProductCard item={product} handleOpen={openPopUp} />
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export { VirtualizedProductGrid }
